import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { StorageUsage } from '@trove/contracts';
import { HardDrives } from '@phosphor-icons/react';
import { api } from '../api';
import { loadCliGuideDismissed } from '../preferences';
import { storagePercentLabel, storagePressure, storageUsageLabel } from '../storageUsage';
import { SidebarCliGuide } from './SidebarCliGuide';
import { StorageMeter } from './StorageMeter';
import styles from './SidebarAnnouncements.module.css';

function StorageNotice({ usage, inert }: { usage: StorageUsage; inert?: boolean }) {
  const pressure = storagePressure(usage);
  if (pressure === 'ok') return null;

  const title =
    pressure === 'full'
      ? 'Storage full'
      : pressure === 'critical'
        ? 'Storage almost full'
        : 'Storage getting full';
  const detail =
    pressure === 'full'
      ? `Uploads will fail until you free space · ${storageUsageLabel(usage)}`
      : `${storagePercentLabel(usage)} used · ${storageUsageLabel(usage)}`;

  return (
    <div className={`${styles.notice} ${styles[`pressure_${pressure}`] ?? ''}`} role="status">
      <HardDrives className={styles.noticeIcon} size={14} aria-hidden />
      <div className={styles.noticeBody}>
        <div className={styles.noticeTitle}>{title}</div>
        <p className={styles.noticeDetail}>{detail}</p>
        <StorageMeter usage={usage} compact />
        <Link to="/settings" className={styles.noticeLink} tabIndex={inert ? -1 : undefined}>
          Open settings
        </Link>
      </div>
    </div>
  );
}

export function SidebarAnnouncements({ inert }: { inert?: boolean }) {
  const [cliDismissed, setCliDismissed] = useState(() => loadCliGuideDismissed());
  const storage = useQuery({
    queryKey: ['storage'],
    queryFn: () => api<StorageUsage>('/v1/storage'),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const showStorage = storage.data != null && storagePressure(storage.data) !== 'ok';
  if (!showStorage && cliDismissed) return null;

  return (
    <div className={styles.wrap}>
      {showStorage && storage.data ? <StorageNotice usage={storage.data} inert={inert} /> : null}
      <SidebarCliGuide inert={inert} embedded onDismissedChange={setCliDismissed} />
    </div>
  );
}
